import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TabPanel, { Item } from 'devextreme-react/tab-panel';
import DropDownButton from 'devextreme-react/drop-down-button';
import { formatMessage } from 'devextreme/localization';
import SplitterLayout from 'react-splitter-layout';
import styles from './production.module.scss';
import SemiProductsTable from './SemiProductsTable';
import FinishedProductConfirmation from './FinishedProductConfirmation';
import { productionStoringNames } from './constants';
import CorrectionReadyProductsTable from './CorrectionReadyProductsTable';
import CorrectionSemiProductsTable from './CorrectionSemiProductsTable';
import UndercutInputTab from './UndercutInputTab';
import UndercutConfirmationTab from './UndercutConfirmationTab';

const CustomDropdown = React.memo(({ selectedKey, onItemClick, items }) => (
  <div className={styles.dropdownBtn}>
    <DropDownButton
      visible={!!selectedKey}
      stylingMode="text"
      useSelectMode
      selectedItemKey={selectedKey}
      displayExpr={(item) => formatMessage('production')[item] || item}
      items={items}
      text={formatMessage('production')[selectedKey] || selectedKey}
      onItemClick={(e) => onItemClick(e.itemData)}
      width="250px"
    />
  </div>
));

const Production = ({
  plantId,
  semiProductsReducer,
  userReducer: { id },
  tabsReducer: { tabSelect },
  settingsReducer: { settings },
  getSemiProducts,
  inputOperationReducer,
  configuratorReducer: { availableOperations },
  getAvailableOperations,
  getReadyProducts,
  useFactWeight
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [operations, setOperations] = useState([]);

  // Выбираем доступные операции
  useEffect(() => {
    if (availableOperations.length) {
      const options = availableOperations.map((opt) => opt.operationTypeName);
      setOperations(options);
      setSelectedOperation(options[0]);
    }
  }, [availableOperations]);

  /** Фильтры по таблице */
  const [filterDataGrid] = useState({
    dateFrom: '',
    dateTo: '',
    plantId
  });

  /** при переключении вкладок */
  const onSelectionChanged = useCallback(({ name, value }) => {
    if (name === 'selectedIndex') {
      setSelectedIndex(value);
      if (value === 2) {
        getReadyProducts({ plantId });
      }
    }
  }, [getReadyProducts, plantId]);

  useEffect(() => {
    if (settings.stockType === 'ready') {
      setSelectedIndex(2);
    } else {
      setSelectedIndex(0);
    }
  }, [settings.stockType]);

  const itemTitleRender = useCallback((item) => <span>{item.name}</span>, []);

  /** Обновление данных при переключении на вкладку */
  useEffect(() => {
    if (id !== null && tabSelect === 8) {
      if (settings.stockType === 'semi') {
        getSemiProducts({
          ...filterDataGrid,
          plantId
        });
        setSelectedOperation(null);
        getAvailableOperations({ plantId });
      }
      if (selectedIndex === 1) {
        getReadyProducts({ plantId });
      }
    }
  }, [
    filterDataGrid,
    plantId,
    id,
    tabSelect,
    getSemiProducts,
    selectedIndex,
    settings.stockType,
    getReadyProducts,
    getAvailableOperations
  ]);

  const readyProductsDataSource = useMemo(
    () =>
      semiProductsReducer.semiProductsFilled.filter(
        (semiProduct) =>
          semiProduct.weightWaste !== 0 || semiProduct.weightTrim !== 0
      ),
    [semiProductsReducer.semiProductsFilled]
  );

  const renderDropDownSemiProduct = useMemo(
    () => (
      <CustomDropdown
        selectedKey={selectedOperation}
        onItemClick={setSelectedOperation}
        items={operations}
      />
    ),
    [operations, selectedOperation, setSelectedOperation]
  );

  const materialOperationStoringName = useMemo(() => {
    if (selectedOperation === 'inputTrim') {
      return productionStoringNames.dataGridInputTrim;
    }
    if (selectedOperation === 'inputWaste') {
      return productionStoringNames.dataGridInputWaste;
    }
    if (selectedOperation === 'inputUndercut') {
      return productionStoringNames.dataGridInputUndercut;
    }
    if (selectedOperation === 'inputAllWaste') {
      return productionStoringNames.dataGridInputAllWaste;
    }
    return productionStoringNames.dataGridInputReady;
  }, [selectedOperation]);

  const renderDataGridInputSemiProduct = useMemo(
    () => (
      <>
        {selectedOperation === 'inputTrim' && (
          <SemiProductsTable
            data={semiProductsReducer.semiProductsFilled}
            storingName={materialOperationStoringName}
            okRequestModal={semiProductsReducer.okRequestModal}
            loadingModal={semiProductsReducer.loadingModal}
            plantId={plantId}
          />
        )}
        {selectedOperation === 'inputWaste' && (
          <SemiProductsTable
            data={semiProductsReducer.semiProductsFilled}
            storingName={materialOperationStoringName}
            okRequestModal={semiProductsReducer.okRequestModal}
            loadingModal={semiProductsReducer.loadingModal}
            plantId={plantId}
          />
        )}
        {selectedOperation === 'inputReady' && (
          <SemiProductsTable
            data={readyProductsDataSource}
            storingName={materialOperationStoringName}
            okRequestModal={semiProductsReducer.okRequestModal}
            loadingModal={semiProductsReducer.loadingModal}
            plantId={plantId}
            zdvlValidate={false}
            showSubtable
            useFactWeight={useFactWeight}
          />
        )}
        {selectedOperation === 'inputUndercut' && (
          <UndercutInputTab
            data={semiProductsReducer.semiProductsFilled}
            loading={semiProductsReducer.loading}
            plantId={plantId}
          />
        )}
        {selectedOperation === 'inputAllWaste' && (
          <SemiProductsTable
            data={semiProductsReducer.semiProductsFilled}
            storingName={materialOperationStoringName}
            okRequestModal={semiProductsReducer.okRequestModal}
            loadingModal={semiProductsReducer.loadingModal}
            plantId={plantId}
          />
        )}
      </>
    ),
    [
      selectedOperation,
      semiProductsReducer.semiProductsFilled,
      semiProductsReducer.okRequestModal,
      semiProductsReducer.loadingModal,
      semiProductsReducer.loading,
      materialOperationStoringName,
      plantId,
      readyProductsDataSource,
      useFactWeight
    ]
  );

  /** Ввод факта пр-ва (работа с ГП) */
  const renderDataGridInputReadyProduction = useMemo(
    () => () => (
      <>
        <div className={styles.column}>
          <SplitterLayout vertical>
            <div style={{ height: '100%' }}>
              <CorrectionReadyProductsTable plantId={plantId}
                                            useFactWeight={useFactWeight} />
            </div>
            <div style={{ height: '100%' }}>
              <CorrectionSemiProductsTable plantId={plantId} />
            </div>
          </SplitterLayout>
        </div>
      </>
    ),
    [plantId, useFactWeight]
  );

  /** Подтверждение факта пр-ва */
  const renderFinishedProductConfirmation = useMemo(
    () => () => (
      <FinishedProductConfirmation
        plantId={plantId}
        selectedIndex={selectedIndex}
        useFactWeight={useFactWeight}
      />
    ),
    [plantId, selectedIndex, useFactWeight]
  );

  /** Подтверждение подкроя */
  const renderUndercutConfirmation = useMemo(
    () => () => (
      <UndercutConfirmationTab
        plantId={plantId}
        selectedIndex={selectedIndex}
      />
    ),
    [plantId, selectedIndex]
  );

  /** Ввод факта пр-ва (работа с ПФ) */
  const renderDataGridInputSemiProduction = useMemo(
    () => () => (
      <>
        {renderDropDownSemiProduct}
        {renderDataGridInputSemiProduct}
      </>
    ),
    [renderDropDownSemiProduct, renderDataGridInputSemiProduct]
  );

  return (
    <div
      className={
        selectedIndex === 1 || selectedOperation === 'inputReady'
          ? styles.containingScroll
          : styles.containing
      }
    >
      <TabPanel
        selectedIndex={selectedIndex}
        onOptionChanged={onSelectionChanged}
        itemTitleRender={itemTitleRender}
        elementAttr={{ class: styles.tabs }}
      >
        <Item
          tabComponent={() => (
            <span>{formatMessage('production').semiTab}</span>
          )}
          render={renderDataGridInputSemiProduction}
          visible={settings.stockType === 'semi'}
        />
        <Item
          tabComponent={() => (
            <span>{formatMessage('production').undectutConfirmTab}</span>
          )}
          render={renderUndercutConfirmation}
          visible={settings.stockType === 'semi' && operations.includes('inputUndercut')}
        />
        <Item
          tabComponent={() => (
            <span>{formatMessage('production').readyTab}</span>
          )}
          render={renderDataGridInputReadyProduction}
          visible={settings.stockType === 'ready'}
        />
        <Item
          tabComponent={() => (
            <span>{formatMessage('production').readyConfirmationTab}</span>
          )}
          render={renderFinishedProductConfirmation}
          visible={settings.stockType === 'ready'}
        />
      </TabPanel>
    </div>
  );
};

export default React.memo(Production);
